import {
  type unstable_RemoteThreadListAdapter as RemoteThreadListAdapter,
  RuntimeAdapterProvider,
  type ThreadHistoryAdapter,
  type ThreadMessage,
  useThreadListItem,
} from "@assistant-ui/react";
import { createAssistantStream } from "assistant-stream";
import React from "react";
import { CuadraChatClient } from "../lib/cuadraChatClient";
import {
  convertFromCuadraMessage,
  injectArtifactMarkers,
  isSupportedRole,
} from "./messageConverter";
import { artifactStore } from "./artifactStore";
import { chatErrorStore } from "./chatErrorStore";

/**
 * Store mapping of local thread IDs to server chat IDs
 * This is used to update remoteId when server chat ID is received
 */
export const localToRemoteIdMap = new Map<string, string>();

/**
 * Store mapping of message IDs to their chat (remote) IDs.
 * Populated during history loading so the chat model adapter
 * can resolve which chat a conversation belongs to.
 */
export const messageIdToChatIdMap = new Map<string, string>();

/**
 * The remoteId of the thread that is currently active (rendering).
 * Set by the unstable_Provider so the chat model adapter can map
 * server chat IDs back to local thread IDs without a reverse lookup.
 */
export let activeThreadRemoteId: string | undefined;

/** Subscribers notified whenever activeThreadRemoteId changes. */
const activeThreadListeners = new Set<() => void>();

export function subscribeToActiveThread(cb: () => void): () => void {
  activeThreadListeners.add(cb);
  return () => activeThreadListeners.delete(cb);
}

export function getActiveThreadRemoteId(): string | undefined {
  return activeThreadRemoteId;
}

/**
 * Set of chat IDs that have an active SSE stream in progress.
 * Populated by the chat model adapter when a chatId is received during
 * streaming, and cleared in the generator's finally block when the
 * stream completes or is aborted.
 *
 * Used to prevent:
 * - SingleChatProvider from re-fetching and remounting the runtime
 *   when the parent feeds the just-created chatId back as initialThreadId.
 * - The multiChat history adapter from issuing a redundant GET while
 *   the runtime already holds the messages from the active stream.
 */
export const activeStreamingChatIds = new Set<string>();

/**
 * Resolve a remoteId (which may be a __LOCALID_*) to the actual server chat ID.
 * Uses multiple resolution strategies in order of reliability.
 */
function resolveServerId(
  remoteId: string,
  messages?: readonly ThreadMessage[],
): string {
  // 1. Direct lookup in localToRemoteIdMap
  const mapped = localToRemoteIdMap.get(remoteId);
  if (mapped) return mapped;

  // 2. Scan messages for a registered chatId (populated by the chat model
  //    adapter when the SSE stream returns a chat ID)
  if (messages) {
    for (const msg of messages) {
      const chatId = messageIdToChatIdMap.get(msg.id);
      if (chatId) {
        // Back-fill so rename/delete/fetch also benefit
        localToRemoteIdMap.set(remoteId, chatId);
        return chatId;
      }
    }
  }

  // 3. Fall back to the remoteId as-is (valid for pre-existing chats
  //    whose remoteId is already the server chat ID)
  return remoteId;
}

export function createThreadListAdapter(
  client: CuadraChatClient,
  onChatIdReceived?: (localThreadId: string, serverChatId: string) => void,
  enableArtifacts = false,
): RemoteThreadListAdapter & {
  updateRemoteId: (localThreadId: string, serverChatId: string) => void;
} {
  return {
    async list() {
      const all = [];
      const limit = 50;
      let cursor: string | undefined = undefined;
      let safety = 0;

      // Fetch all chats with pagination
      do {
        const response = await client.listChats({ limit, cursor });
        const items = response.items || [];

        if (Array.isArray(items) && items.length > 0) {
          all.push(...items);
        }

        cursor = response.nextCursor || undefined;
        safety++;
      } while (cursor && safety < 50);

      // Map to assistant-ui thread format
      // Filter out deleted chats and chats without IDs
      const threads = all
        .map((chat) => {
          const id = chat.id;
          // Skip if no ID or if deleted
          if (!id || chat.deletedAt) return null;

          return {
            status: "regular" as const,
            remoteId: id,
            title: chat.title || "New Chat",
            createdAt: chat.createdAt ? new Date(chat.createdAt) : new Date(),
            updatedAt: chat.updatedAt ? new Date(chat.updatedAt) : new Date(),
          };
        })
        .filter((t) => t !== null) as Array<{
        status: "regular" | "archived";
        remoteId: string;
        title?: string;
        createdAt: Date;
        updatedAt: Date;
      }>;

      return { threads };
    },

    async initialize(threadId: string) {
      // For LocalRuntime, threadId is already generated locally (e.g., __LOCALID_...)
      // Check if we have a server chat ID for this local thread ID
      const serverChatId = localToRemoteIdMap.get(threadId);
      const remoteId = serverChatId || threadId;

      return { remoteId, externalId: undefined };
    },

    async rename(remoteId: string, newTitle: string) {
      const actualId = resolveServerId(remoteId);
      await client.updateChat(actualId, { title: newTitle });
    },

    async archive(_remoteId: string) {
      // Cuadra API uses soft delete, which we can treat as archive
      // If API adds explicit archive support, implement here
    },

    async unarchive(_remoteId: string) {
      // If Cuadra supports unarchive, implement here
      // Currently not supported
    },

    async delete(remoteId: string) {
      const actualId = resolveServerId(remoteId);
      await client.deleteChat(actualId);
    },

    async generateTitle(remoteId: string, _messages: readonly ThreadMessage[]) {
      const actualId = resolveServerId(remoteId, _messages);

      let title: string | undefined;
      try {
        if (actualId.startsWith("__LOCALID_")) {
          throw new Error("Chat ID not yet resolved to a server ID");
        }
        const result = await client.generateChatTitle(actualId);
        title = result.title;
      } catch (error) {
        // eslint-disable-next-line no-console -- Intentional: surface title-generation failures for debugging
        console.error(
          "[generateTitle] API call failed, falling back to first message",
          error,
        );
        // Fallback: extract from first user message if the endpoint fails
        const firstUserMsg = _messages.find((m) => m.role === "user");
        const rawText = firstUserMsg?.content
          ?.filter(
            (p): p is { type: "text"; text: string } => p.type === "text",
          )
          .map((p) => p.text)
          .join(" ")
          .trim();
        title = rawText
          ? rawText.length > 60
            ? rawText.slice(0, 57) + "..."
            : rawText
          : undefined;
      }

      return createAssistantStream((controller) => {
        const text = controller.addTextPart();
        if (title) {
          text.append(title);
        }
        text.close();
        controller.close();
      });
    },

    async fetch(threadId: string) {
      try {
        const chat = await client.getChat(threadId);
        const id = chat.id || threadId;

        return {
          status: "regular" as const,
          remoteId: id,
          title: chat.title || "Chat",
          createdAt: chat.createdAt ? new Date(chat.createdAt) : new Date(),
          updatedAt: chat.updatedAt ? new Date(chat.updatedAt) : new Date(),
        };
      } catch {
        // Return default if fetch fails
        return {
          status: "regular" as const,
          remoteId: threadId,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
      }
    },

    // Add a method to update the remoteId mapping when chat ID is received
    updateRemoteId(localThreadId: string, serverChatId: string) {
      localToRemoteIdMap.set(localThreadId, serverChatId);
      onChatIdReceived?.(localThreadId, serverChatId);
    },

    // Provider component that adds thread-specific history adapter
    unstable_Provider: ({ children }: { children?: React.ReactNode }) => {
      const threadListItem = useThreadListItem();
      const remoteId = threadListItem?.remoteId;
      const isMain = threadListItem?.isMain;

      // Keep activeThreadRemoteId in sync so the chat model adapter
      // can look it up when a server chat ID arrives during streaming.
      // Only the active (main) thread sets the global — multiple Provider
      // instances are mounted simultaneously (one per thread).
      // We intentionally allow clearing to `undefined` when a new thread
      // is created (remoteId is undefined for local-only threads).
      React.useEffect(() => {
        if (isMain) {
          activeThreadRemoteId = remoteId;
          activeThreadListeners.forEach((cb) => cb());
        }
      }, [remoteId, isMain]);

      // Create thread-specific history adapter
      const history = React.useMemo<ThreadHistoryAdapter>(
        () => ({
          async load(): Promise<{
            messages: Array<{
              message: ThreadMessage;
              parentId: string | null;
            }>;
          }> {
            if (!remoteId) return { messages: [] };

            // If this chat has an active SSE stream, skip the GET request.
            // The runtime already holds the messages from the stream; fetching
            // would be redundant and can trigger re-renders that abort the stream.
            if (activeStreamingChatIds.has(remoteId)) {
              return { messages: [] };
            }

            try {
              // Fetch chat with messages (and artifacts when enabled) in parallel
              const [chat, artifactResponse] = await Promise.all([
                client.getChat(remoteId),
                enableArtifacts
                  ? client
                      .listArtifacts(remoteId)
                      .catch(() => ({ items: [], totalCount: 0 }))
                  : Promise.resolve({ items: [], totalCount: 0 }),
              ]);

              if (!chat.messages || chat.messages.length === 0) {
                return { messages: [] };
              }

              // Pre-populate artifact store immediately so clicking a card
              // doesn't show "Creating document…" while the Gate's separate
              // listArtifacts call is still in flight.
              if (artifactResponse.items.length > 0) {
                artifactStore.loadArtifacts(artifactResponse.items);
              }

              // Convert API messages to ExportedMessageRepository format.
              // Filter out role:'tool' result messages — assistant-ui doesn't support them.
              const resolveUrl = (p: string) => client.getUrl(p);
              const supported = chat.messages.filter((msg) =>
                isSupportedRole(msg.role),
              );
              const withMarkers = injectArtifactMarkers(
                supported,
                artifactResponse.items,
              );
              const converted = withMarkers.map((msg) =>
                convertFromCuadraMessage(msg, resolveUrl, remoteId),
              );
              const messages = converted.map((message, idx) => ({
                message,
                parentId: idx > 0 ? converted[idx - 1]!.id : null,
              }));

              // Register every message ID → chatId so the model adapter
              // can resolve which chat this thread belongs to
              for (const msg of converted) {
                messageIdToChatIdMap.set(msg.id, remoteId);
              }

              // Surface chat-level error status for the UI
              if (chat.status === "error" && chat.lastError) {
                chatErrorStore.set({
                  chatId: chat.id ?? remoteId,
                  lastError: chat.lastError,
                  lastErrorAt: chat.lastErrorAt ?? new Date().toISOString(),
                });
              } else {
                chatErrorStore.clear();
              }

              return { messages };
            } catch (_error) {
              return { messages: [] };
            }
          },

          async append(_message) {
            // Messages are automatically persisted by the API when sent
            // This method is called by the runtime but we don't need to do anything
            // since messages are already saved via the chat API
          },
        }),
        [remoteId, client],
      );

      const adapters = React.useMemo(() => ({ history }), [history]);

      return (
        <RuntimeAdapterProvider adapters={adapters}>
          {children}
        </RuntimeAdapterProvider>
      );
    },
  };
}
